import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  CONDITION_OPTIONS,
  DEFAULT_BROWSE_FILTERS,
  DELIVERY_OPTIONS,
  PROXIMITY_OPTIONS,
  RATING_OPTIONS,
  SELLER_TYPE_OPTIONS,
  computePriceRangeHints,
} from '../../utils/catalogBrowseFilters';

function toggleInList(list, key) {
  const set = new Set(list);
  if (set.has(key)) set.delete(key);
  else set.add(key);
  return [...set];
}

function FilterSection({ title, children }) {
  return (
    <section className="border-b border-slate-200 pb-4 mb-4 last:border-0 last:pb-0 last:mb-0">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{title}</h3>
      {children}
    </section>
  );
}

function CheckboxRow({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2.5 py-1.5 cursor-pointer text-sm text-slate-800">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
      />
      <span>{label}</span>
    </label>
  );
}

function RadioRow({ checked, onChange, label, name }) {
  return (
    <label className="flex items-center gap-2.5 py-1.5 cursor-pointer text-sm text-slate-800">
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 border-slate-300 text-indigo-600 focus:ring-indigo-500"
      />
      <span>{label}</span>
    </label>
  );
}

export default function CatalogBrowseFilterPanel({
  filters,
  onChange,
  onClearAll,
  priceHints = { floor: 0, ceiling: 50000 },
  proximityNeedsLocation = false,
  onRequestLocation,
  className = '',
}) {
  const [localMin, setLocalMin] = useState(filters.minPrice || '');
  const [localMax, setLocalMax] = useState(filters.maxPrice || '');
  const [sliderMin, setSliderMin] = useState(
    Number(filters.minPrice) || priceHints.floor
  );
  const [sliderMax, setSliderMax] = useState(
    Number(filters.maxPrice) || priceHints.ceiling
  );

  useEffect(() => {
    if (!filters.minPrice && !filters.maxPrice) {
      setSliderMin(priceHints.floor);
      setSliderMax(priceHints.ceiling);
    }
  }, [priceHints.floor, priceHints.ceiling, filters.minPrice, filters.maxPrice]);

  const applyPrice = useCallback(() => {
    onChange({
      ...filters,
      minPrice: localMin,
      maxPrice: localMax,
    });
  }, [filters, localMin, localMax, onChange]);

  const applySlider = useCallback(
    (minVal, maxVal) => {
      onChange({
        ...filters,
        minPrice: String(minVal),
        maxPrice: String(maxVal),
      });
      setLocalMin(String(minVal));
      setLocalMax(String(maxVal));
    },
    [filters, onChange]
  );

  const floor = priceHints.floor;
  const ceiling = Math.max(priceHints.ceiling, floor + 1);

  return (
    <div className={`flex flex-col h-full ${className}`}>
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
        <span className="text-sm font-semibold text-slate-900">Filters</span>
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
        >
          Clear all filters
        </button>
      </div>

      <div className="flex-1 overflow-y-auto pr-1 -mr-1 space-y-0">
        <FilterSection title="Condition">
          <p className="text-[11px] text-slate-500 mb-2">Multi-select · default All</p>
          {CONDITION_OPTIONS.map((opt) => (
            <CheckboxRow
              key={opt.key}
              label={opt.label}
              checked={filters.conditions.includes(opt.key)}
              onChange={() =>
                onChange({
                  ...filters,
                  conditions: toggleInList(filters.conditions, opt.key),
                })
              }
            />
          ))}
        </FilterSection>

        <FilterSection title="Price range">
          <p className="text-[11px] text-slate-500 mb-2">
            Market ₹{floor.toLocaleString('en-IN')} – ₹{ceiling.toLocaleString('en-IN')}
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-slate-500">Min</label>
              <input
                type="range"
                min={floor}
                max={ceiling}
                value={sliderMin}
                onChange={(e) => {
                  const v = Math.min(Number(e.target.value), sliderMax);
                  setSliderMin(v);
                  applySlider(v, sliderMax);
                }}
                className="w-full accent-indigo-600"
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Max</label>
              <input
                type="range"
                min={floor}
                max={ceiling}
                value={sliderMax}
                onChange={(e) => {
                  const v = Math.max(Number(e.target.value), sliderMin);
                  setSliderMax(v);
                  applySlider(sliderMin, v);
                }}
                className="w-full accent-indigo-600"
              />
            </div>
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="Min ₹"
                value={localMin}
                onChange={(e) => setLocalMin(e.target.value)}
                className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
              />
              <input
                type="number"
                placeholder="Max ₹"
                value={localMax}
                onChange={(e) => setLocalMax(e.target.value)}
                className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={applyPrice}
              className="w-full rounded-lg bg-slate-100 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200"
            >
              Apply price
            </button>
          </div>
        </FilterSection>

        <FilterSection title="Seller type">
          {SELLER_TYPE_OPTIONS.map((opt) => (
            <CheckboxRow
              key={opt.key}
              label={opt.label}
              checked={filters.sellerTypes.includes(opt.key)}
              onChange={() =>
                onChange({
                  ...filters,
                  sellerTypes: toggleInList(filters.sellerTypes, opt.key),
                })
              }
            />
          ))}
        </FilterSection>

        <FilterSection title="Proximity">
          {proximityNeedsLocation && filters.proximity !== 'pan_india' && (
            <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <p className="mb-2">Set your PIN or enable GPS for distance filtering.</p>
              <button
                type="button"
                onClick={onRequestLocation}
                className="font-semibold text-amber-800 underline"
              >
                Use my location
              </button>
            </div>
          )}
          {PROXIMITY_OPTIONS.map((opt) => (
            <RadioRow
              key={opt.key}
              name="proximity"
              label={opt.label}
              checked={filters.proximity === opt.key}
              onChange={() => onChange({ ...filters, proximity: opt.key })}
            />
          ))}
        </FilterSection>

        <FilterSection title="Rating">
          {RATING_OPTIONS.map((opt) => (
            <RadioRow
              key={opt.key}
              name="rating"
              label={opt.label}
              checked={filters.rating === opt.key}
              onChange={() => onChange({ ...filters, rating: opt.key })}
            />
          ))}
        </FilterSection>

        <FilterSection title="Delivery speed">
          <p className="text-[11px] text-slate-500 mb-2">Refines results on this page</p>
          {DELIVERY_OPTIONS.map((opt) => (
            <RadioRow
              key={opt.key}
              name="delivery"
              label={opt.label}
              checked={filters.delivery === opt.key}
              onChange={() => onChange({ ...filters, delivery: opt.key })}
            />
          ))}
        </FilterSection>
      </div>
    </div>
  );
}

export function CatalogBrowseFilterShell({
  open,
  onClose,
  activeCount,
  children,
}) {
  return (
    <>
      {/* Mobile bottom sheet */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-[70] flex flex-col justify-end">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close filters"
            onClick={onClose}
          />
          <div className="relative max-h-[85vh] rounded-t-2xl bg-white shadow-2xl flex flex-col animate-slide-up">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <span className="font-semibold text-slate-900">
                Filters{activeCount > 0 ? ` (${activeCount})` : ''}
              </span>
              <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100">
                <X className="h-5 w-5 text-slate-600" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">{children}</div>
            <div className="p-4 border-t border-slate-200">
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-xl bg-indigo-600 py-3 text-sm font-bold text-white hover:bg-indigo-700"
              >
                Show results
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop side panel — sticky + internal scroll (same as catalog) */}
      <aside className="hidden lg:block w-72 shrink-0 self-start">
        <div className="sticky top-28 z-20 max-h-[calc(100vh-8rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="max-h-[calc(100vh-8rem)] overflow-y-auto overscroll-contain p-4 [scrollbar-width:thin]">
            {children}
          </div>
        </div>
      </aside>
    </>
  );
}

export { DEFAULT_BROWSE_FILTERS, computePriceRangeHints };
